/**
 * DashboardPage: grid of wedding cards + "+ New Wedding" entry point
 * (PRD §14.2). Both Admin and Coordinator see ALL weddings here — a flat
 * rule per the locked access model, not per-row ownership.
 *
 * Upcoming weddings are shown first, ordered by the chosen sort mode —
 * past weddings are shown in a separate, collapsed section, and
 * archived weddings (a distinct, explicit state a coordinator chooses,
 * not just "old") in another, also collapsed by default. Without the
 * upcoming/past split specifically, a venue with even one past wedding
 * on record would see it sort to the very top of a page titled
 * "Upcoming Weddings" forever under a plain date sort, which is exactly
 * backwards from what the page promises.
 */

import { useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useWeddings, useUpdateWedding, useDeleteWedding, useDuplicateWedding } from '../api/weddings';
import { useWeddingIdsWithLayouts } from '../api/layouts';
import { useAuth } from '../auth/AuthProvider';
import type { Wedding } from '../domain/types';
import { WeddingCard } from '../components/dashboard/WeddingCard';
import type { WeddingCardAction } from '../components/dashboard/WeddingCardMenu';
import { NewWeddingWizard } from '../components/wizard/NewWeddingWizard';
import { Toast, ToastStack } from '../components/shared/Toast';

type SortMode = 'event_date' | 'recently_edited' | 'name';

const SORT_LABELS: Record<SortMode, string> = {
  event_date: 'Event date',
  recently_edited: 'Recently edited',
  name: 'Wedding name',
};

function sortWeddings(list: Wedding[], mode: SortMode, dateDirection: 'asc' | 'desc'): Wedding[] {
  const sorted = [...list];
  if (mode === 'name') {
    sorted.sort((a, b) => a.coupleNames.localeCompare(b.coupleNames));
  } else if (mode === 'recently_edited') {
    sorted.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  } else {
    sorted.sort((a, b) =>
      dateDirection === 'asc' ? a.eventDate.localeCompare(b.eventDate) : b.eventDate.localeCompare(a.eventDate)
    );
  }
  return sorted;
}

export function DashboardPage() {
  const { profile, role } = useAuth();
  const { data: weddings, isLoading } = useWeddings();
  const { data: weddingIdsWithLayouts } = useWeddingIdsWithLayouts();
  const updateWedding = useUpdateWedding();
  const deleteWedding = useDeleteWedding();
  const duplicateWedding = useDuplicateWedding();

  const [wizardOpen, setWizardOpen] = useState(false);
  const [showPast, setShowPast] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [sortMode, setSortMode] = useState<SortMode>('event_date');
  const [toast, setToast] = useState<{ message: string; tone: 'success' | 'error' } | null>(null);

  const [renaming, setRenaming] = useState<Wedding | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleting, setDeleting] = useState<Wedding | null>(null);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);

  const { upcoming, past, archived } = useMemo(() => {
    if (!weddings) return { upcoming: [], past: [], archived: [] };
    const todayIso = new Date().toISOString().slice(0, 10);
    const active = weddings.filter((w) => w.status !== 'archived');
    const archivedList = weddings.filter((w) => w.status === 'archived');

    const upcomingList = sortWeddings(active.filter((w) => w.eventDate >= todayIso), sortMode, 'asc');
    const pastList = sortWeddings(active.filter((w) => w.eventDate < todayIso), sortMode, 'desc');
    const archivedSorted = sortWeddings(archivedList, sortMode, 'desc');

    return { upcoming: upcomingList, past: pastList, archived: archivedSorted };
  }, [weddings, sortMode]);

  function handleCardAction(wedding: Wedding, action: WeddingCardAction) {
    if (action === 'rename') {
      setRenaming(wedding);
      setRenameValue(wedding.coupleNames);
    } else if (action === 'archive') {
      updateWedding.mutate(
        { id: wedding.id, status: 'archived' },
        {
          onSuccess: () => setToast({ message: `${wedding.coupleNames} archived.`, tone: 'success' }),
          onError: () => setToast({ message: 'Couldn\u2019t archive that wedding.', tone: 'error' }),
        }
      );
    } else if (action === 'unarchive') {
      updateWedding.mutate(
        { id: wedding.id, status: 'draft' },
        {
          onSuccess: () => setToast({ message: `${wedding.coupleNames} restored.`, tone: 'success' }),
          onError: () => setToast({ message: 'Couldn\u2019t restore that wedding.', tone: 'error' }),
        }
      );
    } else if (action === 'delete') {
      setDeleting(wedding);
    } else if (action === 'duplicate') {
      handleDuplicate(wedding);
    }
  }

  async function handleDuplicate(wedding: Wedding) {
    if (!profile) return;
    if (!weddingIdsWithLayouts?.has(wedding.id)) {
      setToast({
        message: 'This wedding doesn\u2019t have a layout yet, so there\u2019s nothing to duplicate.',
        tone: 'error',
      });
      return;
    }
    setDuplicatingId(wedding.id);
    try {
      const copy = await duplicateWedding.mutateAsync({ source: wedding, createdBy: profile.id });
      setToast({ message: `Created a copy: ${copy.coupleNames}.`, tone: 'success' });
    } catch {
      setToast({ message: 'Something went wrong duplicating that wedding.', tone: 'error' });
    } finally {
      setDuplicatingId(null);
    }
  }

  function handleRenameSubmit(e: FormEvent) {
    e.preventDefault();
    if (!renaming) return;
    const trimmed = renameValue.trim();
    if (!trimmed) return;

    updateWedding.mutate(
      { id: renaming.id, coupleNames: trimmed },
      {
        onSuccess: () => setToast({ message: 'Renamed.', tone: 'success' }),
        onError: () => setToast({ message: 'Couldn\u2019t rename that wedding.', tone: 'error' }),
      }
    );
    setRenaming(null);
  }

  async function handleDeleteConfirmed() {
    if (!deleting) return;
    const wedding = deleting;
    setDeleting(null);
    try {
      await deleteWedding.mutateAsync(wedding.id);
      setToast({ message: `${wedding.coupleNames} deleted.`, tone: 'success' });
    } catch {
      setToast({ message: 'Something went wrong deleting that wedding.', tone: 'error' });
    }
  }

  return (
    <div className="mx-auto max-w-7xl px-6 py-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-2xl text-ink-700">Upcoming Weddings</h1>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 font-body text-sm text-ink-500">
            Sort by
            <select
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value as SortMode)}
              className="rounded border border-ivory-300 bg-white px-2 py-1.5 font-body text-sm text-ink-700"
            >
              {Object.entries(SORT_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <Link
            to="/templates"
            className="font-body text-sm text-evergreen-600 underline-offset-2 hover:underline"
          >
            Browse templates
          </Link>
          <button
            onClick={() => setWizardOpen(true)}
            className="rounded bg-evergreen-600 px-4 py-2 font-body text-sm font-medium text-ivory-50 hover:bg-evergreen-700"
          >
            + New Wedding
          </button>
        </div>
      </div>

      {isLoading && <p className="mt-8 font-body text-sm text-ink-400">{'Loading weddings\u2026'}</p>}

      {!isLoading && weddings?.length === 0 && (
        <div className="mt-16 flex flex-col items-center gap-3 text-center">
          <p className="font-display text-lg text-ink-500">No weddings yet</p>
          <p className="font-body text-sm text-ink-400">
            Create your first wedding to start building a reception layout.
          </p>
          <button
            onClick={() => setWizardOpen(true)}
            className="mt-2 rounded bg-evergreen-600 px-4 py-2 font-body text-sm font-medium text-ivory-50 hover:bg-evergreen-700"
          >
            + New Wedding
          </button>
        </div>
      )}

      {!isLoading && weddings && weddings.length > 0 && upcoming.length === 0 && (
        <p className="mt-8 font-body text-sm text-ink-400">
          No upcoming weddings on the calendar right now.
        </p>
      )}

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {upcoming.map((wedding) => (
          <div key={wedding.id} className="relative">
            <WeddingCard wedding={wedding} canDelete={role === 'admin'} onAction={handleCardAction} />
            {duplicatingId === wedding.id && (
              <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-white/70">
                <p className="font-body text-sm text-ink-500">{'Duplicating\u2026'}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {past.length > 0 && (
        <div className="mt-10 border-t border-ivory-200 pt-6">
          <button
            onClick={() => setShowPast((s) => !s)}
            className="font-body text-sm text-ink-400 hover:text-ink-600"
          >
            {showPast ? '\u25B4' : '\u25BE'} Past weddings ({past.length})
          </button>

          {showPast && (
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {past.map((wedding) => (
                <WeddingCard key={wedding.id} wedding={wedding} canDelete={role === 'admin'} onAction={handleCardAction} />
              ))}
            </div>
          )}
        </div>
      )}

      {archived.length > 0 && (
        <div className="mt-6 border-t border-ivory-200 pt-6">
          <button
            onClick={() => setShowArchived((s) => !s)}
            className="font-body text-sm text-ink-400 hover:text-ink-600"
          >
            {showArchived ? '\u25B4' : '\u25BE'} Archived ({archived.length})
          </button>

          {showArchived && (
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {archived.map((wedding) => (
                <WeddingCard key={wedding.id} wedding={wedding} canDelete={role === 'admin'} onAction={handleCardAction} />
              ))}
            </div>
          )}
        </div>
      )}

      {wizardOpen && <NewWeddingWizard onClose={() => setWizardOpen(false)} />}

      {/* Rename modal */}
      {renaming && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-700/40 px-4">
          <div className="w-full max-w-sm rounded-xl bg-ivory-50 p-6 shadow-elevated">
            <h2 className="font-display text-lg text-ink-700">Rename wedding</h2>
            <form onSubmit={handleRenameSubmit} className="mt-4">
              <input
                type="text"
                autoFocus
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                className="w-full rounded border border-ivory-300 bg-white px-3 py-2 font-body text-ink-700 outline-none focus:border-gold-400 focus:ring-2 focus:ring-gold-200"
              />
              <div className="mt-4 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setRenaming(null)}
                  className="rounded px-4 py-2 font-body text-sm text-ink-500 hover:bg-ivory-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded bg-evergreen-600 px-4 py-2 font-body text-sm font-medium text-ivory-50 hover:bg-evergreen-700"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirmation — always required before permanently
          removing a wedding, per the product brief. */}
      {deleting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-700/40 px-4">
          <div className="w-full max-w-sm rounded-xl bg-ivory-50 p-6 shadow-elevated">
            <h2 className="font-display text-lg text-ink-700">Delete this wedding?</h2>
            <p className="mt-2 font-body text-sm text-ink-500">
              This permanently removes {deleting.coupleNames} and its floor plan. This can&rsquo;t be
              undone \u2014 if you just want it out of the way, Archive instead.
            </p>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                onClick={() => setDeleting(null)}
                className="rounded px-4 py-2 font-body text-sm text-ink-500 hover:bg-ivory-100"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirmed}
                className="rounded bg-danger px-4 py-2 font-body text-sm font-medium text-ivory-50 hover:bg-danger/90"
              >
                Delete wedding
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastStack>
        {toast && <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />}
      </ToastStack>
    </div>
  );
}
